<script setup>
import { reactive, ref } from 'vue'
import EmailFieldEditor from './EmailFieldEditor.vue'

// The quote-email template editor: ONE main-document editor (all the static
// prose, with ▦ chips where the dynamic blocks land) + one card per dynamic
// block whose labelled fields hold every conditional variant / row template.
//
// `editorState` comes from SettingsController#email_editor_state. Each child
// writes a hidden input INSIDE the surrounding <form> (this component mounts
// into a div within form_with), so a normal submit carries every editor's
// serialised `{{token}}` string — blank meaning "use the built-in default".
//
// Preview renders the WHOLE email server-side from the full current draft
// state (main + every field), so it always shows exactly what saving now
// would produce.
const props = defineProps({
  editorState: { type: Object, required: true },
  previewUrl:  { type: String, required: true },
})

// name -> current draft value ("" = use default), kept in sync by child
// change events; seeded from the saved overrides.
const draft = reactive({})
draft[props.editorState.main.name] = props.editorState.main.content ?? ''
for (const block of props.editorState.blocks) {
  for (const field of block.fields) draft[field.name] = field.content ?? ''
}

function onChange(name, value) {
  draft[name] = value
}

const showPreview = ref(false)
const previewHtml = ref('')
const previewLoading = ref(false)

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content ?? ''
}

async function refreshPreview() {
  previewLoading.value = true
  try {
    const fd = new FormData()
    for (const [name, value] of Object.entries(draft)) {
      fd.append(`email_sections[${name}]`, value)
    }
    const r = await fetch(props.previewUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      body: fd,
    })
    previewHtml.value = await r.text()
  } catch (err) {
    previewHtml.value = `<p style="color:#b91c1c">Preview failed: ${err.message}</p>`
  } finally {
    previewLoading.value = false
  }
}

async function togglePreview() {
  showPreview.value = !showPreview.value
  if (showPreview.value) await refreshPreview()
}
</script>

<template>
  <div class="email-sections-editor">
    <div class="flex items-center justify-between mb-2">
      <h2 class="font-medium text-sm">Your quote email</h2>
      <button type="button" class="btn btn-ghost btn-xs" data-preview-email @click="togglePreview">
        {{ showPreview ? 'Hide preview' : 'Preview' }}
      </button>
    </div>

    <EmailFieldEditor
      :name="editorState.main.name"
      :content="editorState.main.content"
      :default-content="editorState.main.default"
      :required-tokens="editorState.main.required_tokens || []"
      :allowed-tokens="editorState.main.allowed_tokens || []"
      mode="block"
      main
      @change="onChange"
    />

    <!-- Deliberately bg-white in BOTH themes: the preview shows the email as
         the customer receives it, and the quote email is always
         light-background HTML. The labels inside the pane use fixed greys
         (not base-content) so they stay legible on white in dark mode. -->
    <div v-if="showPreview" class="mt-3 border border-base-300 rounded bg-white p-3 text-gray-700">
      <div class="flex items-center justify-between mb-2">
        <span class="text-xs font-medium text-gray-500">Preview — whole email with sample data</span>
        <button type="button" class="btn btn-ghost btn-xs" @click="refreshPreview">Refresh</button>
      </div>
      <p v-if="previewLoading" class="text-xs text-gray-400 italic">Rendering…</p>
      <div v-else class="email-prose" v-html="previewHtml"></div>
    </div>

    <h2 class="font-medium text-sm mt-6 mb-1">Dynamic content</h2>
    <p class="text-xs text-base-content/50 mb-3">
      The wording used inside the ▦ chips above. When each piece appears
      (VIC-only, shell vs kit, installer entered, one line per pool) is decided
      automatically per quote.
    </p>

    <details v-for="block in editorState.blocks" :key="block.key"
      class="border border-base-300 rounded mb-3">
      <summary class="cursor-pointer px-4 py-2 font-medium text-sm bg-base-200">
        ▦ {{ block.title }}
      </summary>
      <div class="p-4 space-y-4">
        <EmailFieldEditor
          v-for="field in block.fields"
          :key="field.name"
          :name="field.name"
          :label="field.label"
          :content="field.content"
          :default-content="field.default"
          :required-tokens="field.required_tokens || []"
          :allowed-tokens="field.allowed_tokens || []"
          :mode="field.mode"
          @change="onChange"
        />
      </div>
    </details>
  </div>
</template>
